import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  unwrapTrpcData,
} from '@org/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type LearningPathRow = RouterOutputs['learningPaths']['list'][number];

// Radix Select cannot hold an empty-string value, so the "use the default
// path" choice is a sentinel that we translate back to `null` on change.
const DEFAULT_PATH_VALUE = '__default_path__';

type LearningPathSelectProps = {
  value: string | null | undefined;
  onChange: (pathId: string | null) => void;
  disabled?: boolean;
};

/**
 * Optional "Learning path" picker shared by the student and group forms.
 * Blank (the first option) means "follow the default path" and is stored as
 * `pathId = null`.
 */
export function LearningPathSelect({ value, onChange, disabled }: LearningPathSelectProps) {
  const { t } = useTranslation('admin');
  const { data } = trpc.learningPaths.list.useQuery();

  const options = useMemo(() => {
    const paths = unwrapTrpcData<LearningPathRow[]>(data as LearningPathRow[]) ?? [];
    // Hide inactive paths unless one is already selected on this record.
    return paths.filter((path) => path.isActive || path.pathId === value);
  }, [data, value]);

  return (
    <Select
      value={value ?? DEFAULT_PATH_VALUE}
      onValueChange={(selected) => onChange(selected === DEFAULT_PATH_VALUE ? null : selected)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full text-left">
        <SelectValue placeholder={t('learningPaths.select.label')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={DEFAULT_PATH_VALUE}>{t('learningPaths.select.defaultOption')}</SelectItem>
        {options.map((path) => (
          <SelectItem key={path.pathId} value={path.pathId}>
            {path.name}
            {path.isDefault ? ` (${t('learningPaths.list.defaultBadge')})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
